import React from 'react';
import {
    ButtonGroup,
    cssVar,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Stack,
    useToast,
    Button,
    CheckboxGroup,
    Text,
    Checkbox,
} from '@cad-arc';
import { AgencyProfileOption, EventResource, FieldNameEnum } from '@mark43/cad-api';
import { useBootstrapState } from '../../../../../bootstrap/bootstrap-provider';
import { useState } from 'react';
import { useEnabledFeatures } from '../../../../../common/hooks/enabled-features/use-enabled-features';
import { createApiErrorHandler } from '@m43/core';
import { useApiClient } from '~/api-client';
import { useMutation } from '@tanstack/react-query';
import { errorOccurredWhile } from '../../../../../common/helpers/error-occurred-while';

function useGenerateBulkReportingEventNumber() {
    const enabledFeatures = useEnabledFeatures();
    const apiClient = useApiClient();
    const toast = useToast();

    const { fieldDisplayNameByFieldName: fieldDisplayNames } = useBootstrapState();

    const renDisplayName =
        fieldDisplayNames[FieldNameEnum.AGENCY_EVENT_REPORTING_EVENT_NUMBER.name];

    const bulkGenerateOtherAgencyRenForAgencyEventV2 = useMutation(
        (params: { agencyEventId: number; agencyIds: number[] }) => {
            return apiClient.request<EventResource.CreateRensForAgencyEventV2>({
                method: 'PUT',
                url: `event/v2/agency/ren/${params.agencyEventId}/bulk`,
                data: params.agencyIds,
            });
        }
    );

    const bulkGenerateOtherAgencyRenForAgencyEvent = useMutation(
        (params: { agencyEventId: number; agencyIds: number[] }) => {
            return apiClient.request<EventResource.CreateRensForAgencyEvent>({
                method: 'PUT',
                url: `event/agency/ren/${params.agencyEventId}/bulk`,
                data: params.agencyIds,
            });
        }
    );

    const isLoading = [
        bulkGenerateOtherAgencyRenForAgencyEventV2,
        bulkGenerateOtherAgencyRenForAgencyEvent,
    ].some((mutation) => mutation.isLoading);

    return [
        ({
            agencyEventId,
            agencyIds,
            agencyEventNumber,
        }: {
            agencyEventNumber: string;
            agencyEventId: number;
            agencyIds: number[];
        }) => {
            function makeRequest() {
                if (enabledFeatures.multiRenGeneration) {
                    return bulkGenerateOtherAgencyRenForAgencyEventV2.mutateAsync({
                        agencyEventId,
                        agencyIds,
                    });
                } else {
                    return bulkGenerateOtherAgencyRenForAgencyEvent.mutateAsync({
                        agencyEventId,
                        agencyIds,
                    });
                }
            }

            return makeRequest()
                .then(() => {
                    toast({
                        status: 'default',
                        description: `${renDisplayName} generated for ${agencyEventNumber}`,
                    });
                })
                .catch(
                    createApiErrorHandler(() => {
                        toast({
                            status: 'error',
                            description: errorOccurredWhile(`generating ${renDisplayName}`),
                        });
                    })
                );
        },
        { isLoading },
    ] as const;
}

export function GenerateOtherAgencyReportingEventNumberModal(props: {
    eventId: number;
    onClose: () => void;
    agencyEventNumber: string;
    agencyProfileOptions: AgencyProfileOption[];
}): React.ReactElement {
    const [checkboxGroupState, setCheckboxGroupState] = useState<string[]>([]);

    const { eventId, onClose, agencyEventNumber, agencyProfileOptions } = props;

    const { fieldDisplayNameByFieldName: fieldDisplayNames } = useBootstrapState();

    const renDisplayName =
        fieldDisplayNames[FieldNameEnum.AGENCY_EVENT_REPORTING_EVENT_NUMBER.name];

    const [generateBulkReportingEventNumber, { isLoading }] = useGenerateBulkReportingEventNumber();

    return (
        <>
            <ModalHeader>Generate {renDisplayName}</ModalHeader>
            <ModalBody>
                <Stack maxHeight={200} overflow="auto">
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        position="sticky"
                        top={0}
                        zIndex={1}
                        backgroundColor={cssVar('arc.colors.surface.foreground')}
                    >
                        <Text lineHeight={1.5} variant="headingSm">
                            Involved Agencies
                        </Text>
                        {checkboxGroupState.length > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setCheckboxGroupState([])}
                            >
                                Clear All
                            </Button>
                        )}
                    </Stack>
                    <CheckboxGroup
                        value={checkboxGroupState}
                        direction="column"
                        onChange={(values) =>
                            setCheckboxGroupState(values.map((value) => String(value)))
                        }
                    >
                        {agencyProfileOptions.map(({ value, label }) => {
                            return (
                                <Checkbox key={value} value={String(value)}>
                                    {label}
                                </Checkbox>
                            );
                        })}
                    </CheckboxGroup>
                </Stack>
            </ModalBody>
            <ModalFooter>
                <ButtonGroup>
                    <Button variant="ghost" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        isLoading={isLoading}
                        variant="solid"
                        disabled={checkboxGroupState.length === 0}
                        onClick={() => {
                            if (eventId && agencyEventNumber) {
                                generateBulkReportingEventNumber({
                                    agencyEventNumber,
                                    agencyEventId: eventId,
                                    agencyIds: checkboxGroupState.map((value) =>
                                        parseInt(value, 10)
                                    ),
                                }).finally(() => {
                                    onClose();
                                });
                            }
                        }}
                    >
                        Generate
                    </Button>
                </ButtonGroup>
            </ModalFooter>
        </>
    );
}
